/**
 * CopilotSidebar — the left column on /copilot/<threadId>: a way back to
 * Copilot Home, and the results this chat has produced, each opening in the
 * results area. Switching chats lives in the chat's own header.
 *
 *  ┌──────────────────────────┐
 *  │  ←  Copilot Home         │
 *  ├──────────────────────────┤
 *  │  IN THIS SESSION       4 │
 *  │  ▦ 500 most recent leads │
 *  │    500 rows · 2 min ago  │
 *  │  ▥ Conversion by week  • │  ← new since last opened
 *  └──────────────────────────┘
 */

import React from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import { ItemList } from '../../../ui-kit';
import { useCopilotStore } from '../stores/useCopilotStore';
import type { WorkspaceArtifact } from '../stores/useCopilotStore';
import { formatRelativeTime } from '../../../utils/date';

interface CopilotSidebarProps {
  collapsed: boolean;
}

const ICONS: Partial<Record<WorkspaceArtifact['type'], React.ElementType>> = {
  query_result: TableChartOutlined,
  chart: BarChartRounded,
  metric_card: SpeedRounded,
  document: ArticleOutlined,
};

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * What kind of result it is and when it arrived — "63 rows · 6 days ago",
 * "Heatmap chart · 6 days ago". The source is left to the result itself, so
 * the line stays short enough to read in a narrow column.
 */
function resultMeta(a: WorkspaceArtifact): string {
  const kind =
    a.type === 'query_result'
      ? `${a.rowCount.toLocaleString()} row${a.rowCount === 1 ? '' : 's'}`
      : a.type === 'chart'
        ? `${capitalise(a.chartType || 'chart').replace(/ chart$/i, '')} chart`
        : a.type === 'metric_card'
          ? 'Metric'
          : a.type === 'document'
            ? `${(a.format || 'docx').toUpperCase()} document`
            : 'Result';
  const when = a.createdAt ? formatRelativeTime(a.createdAt) : '';
  return [kind, when].filter(Boolean).join(' · ');
}

export function CopilotSidebar({ collapsed }: CopilotSidebarProps) {
  const navigate = useNavigate();
  const artifacts = (useCopilotStore((s: any) => s.workspaceTabs) as WorkspaceArtifact[]).filter(
    (a) => a.type !== 'finding',
  );
  const activeTabId = useCopilotStore((s: any) => s.activeTabId) as string | null;
  const setActiveTab = useCopilotStore((s: any) => s.setActiveTab) as (id: string | null) => void;
  const seenArtifactIds = useCopilotStore((s: any) => s.seenArtifactIds) as Set<string>;
  const isLoadingHistory = useCopilotStore((s: any) => s.isLoadingHistory) as boolean;

  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
        <Tooltip title="Copilot Home" placement="right">
          <IconButton
            size="small"
            aria-label="Copilot Home"
            onClick={() => navigate('/copilot')}
            sx={(t) => ({ color: t.tokens.sidebar.itemFg, '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg } })}
          >
            <HomeRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    // Fill the space the app shell gives it — the column shares its height with
    // Logout and "Talk to our team" below, so a fixed height ran under them.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={(t) => ({ p: 1, flexShrink: 0, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <ButtonBase
          onClick={() => navigate('/copilot')}
          sx={(t) => ({
            width: '100%',
            justifyContent: 'flex-start',
            gap: 1,
            px: 1,
            height: t.ds.sizes.control.md,
            borderRadius: `${t.ds.radius.sm}px`,
            color: t.tokens.sidebar.itemFg,
            '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
            '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
          })}
        >
          <ArrowBackRounded fontSize="small" />
          <Typography variant="subtitle2" component="span" sx={{ color: 'inherit' }}>
            Copilot Home
          </Typography>
        </ButtonBase>
      </Box>

      <ItemList
        heading="In this session"
        items={[...artifacts].reverse().map((a) => {
          const Icon = ICONS[a.type] ?? TableChartOutlined;
          return {
            id: a.id,
            title: a.title || 'Result',
            meta: resultMeta(a),
            icon: <Icon fontSize="small" />,
            unread: !seenArtifactIds.has(a.id),
          };
        })}
        activeId={activeTabId}
        onSelect={setActiveTab}
        loading={isLoadingHistory}
        emptyMessage="No results yet. Ask Copilot a question to start."
      />
    </Box>
  );
}

export default CopilotSidebar;
